import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { BASE_API_PATH } from '../constants';

const CONTACT_ITEMS = [
    {
        label: 'Location',
        value: 'Seattle, Washington',
        icon: <FmdGoodOutlinedIcon sx={{ color: '#2c62bf', fontSize: 22 }} />,
    },
    {
        label: 'Open to',
        value: 'Collaborations, side projects, and conversations',
        icon: <MailOutlineRoundedIcon sx={{ color: '#2c62bf', fontSize: 22 }} />,
    },
    {
        label: 'Response Time',
        value: 'No guarantees, but usually within a week',
        icon: <AccessTimeIcon sx={{ color: '#2c62bf', fontSize: 22 }} />,
    },
];

export function Contact() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');

    useEffect(() => {
        document.title = 'Contact - Mike Brauninger';
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }, []);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitError('');
        setSubmitSuccess('');
        setIsSending(true);

        try {
            const response = await fetch(`${BASE_API_PATH}/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: name.trim(),
                    email: email.trim(),
                    subject: subject.trim(),
                    message: message.trim(),
                }),
            });

            if (!response.ok) {
                const errorMessage = await response.text();
                throw new Error(errorMessage || 'Something went wrong while sending your message.');
            }

            setSubmitSuccess('Message sent successfully. Thank you for reaching out!');
            setName('');
            setEmail('');
            setSubject('');
            setMessage('');
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Failed to send message.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 1200,
                mx: 'auto',
                px: { xs: 2, md: 4 },
                pt: { xs: 2, md: 3 },
                pb: { xs: 5, md: 7 },
            }}
        >
            <Grid
                container
                spacing={{ xs: 2.5, md: 4 }}
                sx={{
                    mt: { xs: 0.5, md: 1.5 },
                    alignItems: 'flex-start',
                }}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        flexBasis: { md: '46%' },
                        maxWidth: { md: '46%' },
                    }}
                >
                    <Typography
                        sx={{
                            color: '#2d63be',
                            fontWeight: 700,
                            fontSize: 16,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Get in touch
                    </Typography>
                    <Typography
                        sx={{
                            mt: 1,
                            color: '#0f2f68',
                            fontWeight: 700,
                            fontSize: { xs: 44, md: 58 },
                            lineHeight: 1.02,
                        }}
                    >
                        Want to chat?
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            color: '#365685',
                            fontSize: { xs: 20, md: 18 },
                            lineHeight: 1.6,
                            maxWidth: 580,
                        }}
                    >
                        I&apos;m always open to conversations, so feel free to reach out. As a disclaimer, I read every message, but I may not be able to reply to all of them.
                    </Typography>
                    <Box sx={{ mt: 2.4 }}>
                        {CONTACT_ITEMS.map((item, index) => (
                            <Box
                                key={item.label}
                                sx={{
                                    py: 1.6,
                                    borderBottom: index === CONTACT_ITEMS.length - 1 ? 'none' : '1px solid #dfe7f5',
                                    display: 'flex',
                                    gap: 1.7,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        backgroundColor: '#edf4ff',
                                        border: '1px solid #dbe8fb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#1a3769', fontSize: 19, fontWeight: 600 }}>{item.label}</Typography>
                                    <Typography sx={{ color: '#2f5ea8', fontSize: 15.5, mt: 0.2 }}>{item.value}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        flexBasis: { md: '54%' },
                        maxWidth: { md: '54%' },
                    }}
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            borderRadius: 3,
                            border: '1px solid #dce8f8',
                            backgroundColor: '#fdfefe',
                            boxShadow: '0px 10px 30px rgba(20, 64, 133, 0.08)',
                        }}
                    >
                        <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: { xs: 28, md: 44 }, mb: 2.1 }}>
                            Send a message
                        </Typography>
                        <Stack spacing={1.8}>
                            <Box>
                                <Typography sx={{ color: '#1f2937', fontSize: 16.5, fontWeight: 500, mb: 0.65 }}>
                                    Name
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Your name"
                                    variant="outlined"
                                    inputProps={{ 'aria-label': 'Your name' }}
                                    size="small"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    required
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ color: '#1f2937', fontSize: 16.5, fontWeight: 500, mb: 0.65 }}>
                                    Email
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Your email"
                                    variant="outlined"
                                    type="email"
                                    inputProps={{ 'aria-label': 'Your email' }}
                                    size="small"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    required
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ color: '#1f2937', fontSize: 16.5, fontWeight: 500, mb: 0.65 }}>
                                    Subject
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="What's this about?"
                                    variant="outlined"
                                    inputProps={{ 'aria-label': "What's this about?" }}
                                    size="small"
                                    value={subject}
                                    onChange={(event) => setSubject(event.target.value)}
                                    required
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ color: '#1f2937', fontSize: 16.5, fontWeight: 500, mb: 0.65 }}>
                                    Message
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Your message..."
                                    variant="outlined"
                                    multiline
                                    minRows={4}
                                    inputProps={{ 'aria-label': 'Your message' }}
                                    value={message}
                                    onChange={(event) => setMessage(event.target.value)}
                                    required
                                />
                            </Box>
                            {submitError ? (
                                <Typography sx={{ color: '#b42318', fontSize: 14 }}>{submitError}</Typography>
                            ) : null}
                            {submitSuccess ? (
                                <Typography sx={{ color: '#15803d', fontSize: 14 }}>{submitSuccess}</Typography>
                            ) : null}
                            <Button
                                type="submit"
                                variant="contained"
                                endIcon={<SendRoundedIcon sx={{ fontSize: 22 }} />}
                                disabled={isSending}
                                sx={{
                                    mt: 0.6,
                                    py: 1.1,
                                    borderRadius: 2,
                                    fontSize: 18,
                                    fontWeight: 600,
                                }}
                            >
                                {isSending ? 'Sending...' : 'Send Message'}
                            </Button>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
